import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { GetVolunteerActivitySummaryResponse } from '@amzn/red-velvet-api';
import { useTimezone } from '../timezone';
import { getEnabledFeatures, useFeatures } from '../features';
import { getClient } from '../../utils/getClient';
import {
  ACTIVITY_SUMMARY_MOCK_PARAM,
  areActivitySummaryMocksAllowed,
  getVolunteerActivitySummaryMock,
} from '../../components/activitySummary/mocks/volunteerActivitySummaryFixtures';

export const VOLUNTEER_ACTIVITY_SUMMARY_QUERY_KEY = ['RedVelvetApi', 'getVolunteerActivitySummary'];

/**
 * The caller's own Volunteer summary, for "this year" in their browser's time zone.
 *
 * Adding `?activitySummaryMock=<fixture>` swaps in a mock fixture instead of calling the API, to
 * review edge and failure states. That only works on local, dev, alpha, ITL targets, and personal
 * stacks; beta and prod always call the API.
 */
export function useVolunteerActivitySummary() {
  const timeZone = useTimezone();
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));
  const mockFixture = areActivitySummaryMocksAllowed()
    ? searchParams.get(ACTIVITY_SUMMARY_MOCK_PARAM)
    : null;

  return useQuery<GetVolunteerActivitySummaryResponse>({
    queryKey: [...VOLUNTEER_ACTIVITY_SUMMARY_QUERY_KEY, { timeZone, mockFixture }],
    queryFn: async () =>
      mockFixture !== null
        ? getVolunteerActivitySummaryMock(mockFixture)
        : client.getVolunteerActivitySummary({ timeZone }),
    // The panel hides on failure and never shows an error, so retrying only delays that.
    retry: false,
  });
}
